import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/constants/queryKeys";
import useAuthStore from "@/context/auth/useAuthStore";
import useSuperAdminStore from "@/context/superadmin/useSuperAdminStore";
import { useInvalidateOrgQueries } from "@/hooks/useInvalidateOrgQueries";
import { services } from "@/services/registry";
import { CustomerCombobox } from "@/components/ui/CustomerCombobox";
const ALL_ORGS_ID = "ALL_ORGS";
const ALL_ORGS_OPTION = {
    id: ALL_ORGS_ID,
    name: "All Organizations",
    email: "View aggregate data"
};
export default function OrganizationSelector() {
    const { customerIdentifier } = useAuthStore();
    const { getActiveCustomerId, setSelectedCustomer } = useSuperAdminStore();
    const { invalidateAllOrgQueries } = useInvalidateOrgQueries();
    const [isChanging, setIsChanging] = useState(false);
    const activeValue = getActiveCustomerId(customerIdentifier, ALL_ORGS_ID);
    const { data, isLoading, isError } = useQuery({
        queryKey: queryKeys.organizations.all(),
        queryFn: ()=>services.organizationService.getAllOrganizations()
    });
    const handleOrganizationChange = async (value)=>{
        if (!value || value === activeValue) return;
        setIsChanging(true);
        try {
            await invalidateAllOrgQueries();
            if (value === ALL_ORGS_ID) {
                setSelectedCustomer(null);
            } else {
                setSelectedCustomer(value);
            }
        } finally{
            setIsChanging(false);
        }
    };
    const groups = useMemo(()=>{
        if (!data?.organizations) return [];
        const organizations = data.organizations;
        const currentOrg = organizations.find((org)=>org.customerId === customerIdentifier);
        const otherOrgs = organizations.filter((org)=>org.customerId !== customerIdentifier);
        const result = [];
        if (currentOrg) {
            result.push({
                label: "Current",
                items: [
                    {
                        id: currentOrg.customerId,
                        name: currentOrg.name,
                        email: currentOrg.email
                    }
                ]
            });
        }
        result.push({
            label: "Options",
            items: [
                ALL_ORGS_OPTION
            ]
        });
        if (otherOrgs.length > 0) {
            result.push({
                label: "Organizations",
                items: otherOrgs.map((org)=>({
                        id: org.customerId,
                        name: org.name,
                        email: org.email
                    }))
            });
        }
        return result;
    }, [
        data,
        customerIdentifier
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-white text-sm",
            children: "Loading organizations..."
        });
    }
    if (isError || !data) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx(CustomerCombobox, {
                customers: [],
                groups: groups,
                value: activeValue,
                onChange: handleOrganizationChange,
                disabled: isChanging,
                placeholder: "Search organizations...",
                inputClassName: "bg-navy-80 text-white border border-navy-60 rounded text-sm placeholder:text-navy-30 mb-0",
                panelClassName: "!max-h-72"
            }),
            isChanging && /*#__PURE__*/ _jsx("span", {
                className: "text-xs text-white",
                children: "Loading..."
            })
        ]
    });
}
